import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { hasPermission, hasRole } from '../../utils/roleManager';

const ProtectedRoute = ({ 
  children, 
  requiredRole = null, 
  requiredPermission = null,
  allowedRoles = null,
  fallback = null 
}) => {
  const { currentUser, loading } = useAuth();
  const navigate = useNavigate();

  // Add timeout fallback for loading state (must be outside conditional)
  useEffect(() => {
    if (!loading) return; // Early return if not loading
    
    const timeoutId = setTimeout(() => {
      console.warn('ProtectedRoute: Loading timeout after 10 seconds');
      console.log('Current state:', { currentUser, loading });
    }, 10000);
    
    return () => clearTimeout(timeoutId);
  }, [loading, currentUser]);

  // ตรวจสอบว่าผู้ใช้มี role หรือยัง (ถ้ายังไม่มี role = ยังไม่สมัครสมาชิก)
  // ต้องเรียก useEffect ก่อน early return เพื่อไม่ให้ผิดกฎของ React Hooks
  useEffect(() => {
    // ตรวจสอบว่าอยู่ในหน้า register หรือ public routes หรือไม่ (เพื่อป้องกัน redirect loop)
    const isRegisterPage = window.location.pathname === '/register';
    const isNurseApp = window.location.pathname.startsWith('/nurse');
    const isPublicRoute = window.location.pathname.startsWith('/pharma-quiz') || 
                          window.location.pathname.startsWith('/mothai-quiz') ||
                          window.location.pathname === '/' ||
                          window.location.pathname === '/login';
    
    if (currentUser && !currentUser.role && !isRegisterPage && !isPublicRoute) {
      // ถ้ายังไม่มี role และไม่ได้อยู่ในหน้า register หรือ public routes → redirect ไปหน้า register
      // redirect ไป /register
      const registerPath = '/register?complete=true';
      console.log('User not registered yet, redirecting to register...', registerPath);
      navigate(registerPath, { 
        state: { 
          fromGoogle: true,
          email: currentUser.email,
          displayName: currentUser.displayName 
        },
        replace: true // ใช้ replace เพื่อไม่ให้เกิด redirect loop
      });
    }
  }, [currentUser, navigate]);

  // แสดง loading ขณะตรวจสอบ authentication
  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <div className="text-lg text-gray-600 mb-2">กำลังตรวจสอบสิทธิ์...</div>
        <div className="text-sm text-gray-400">กรุณารอสักครู่</div>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
        >
          🔄 Refresh หน้า
        </button>
      </div>
    );
  }

  // ตรวจสอบว่าผู้ใช้ล็อกอินหรือไม่
  if (!currentUser) {
    return (
      <div className="p-6 text-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p className="font-semibold">❌ กรุณาล็อกอินก่อน</p>
          <p className="text-sm mt-1">คุณต้องล็อกอินเพื่อเข้าถึงหน้านี้</p>
        </div>
      </div>
    );
  }

  // ถ้ายังไม่มี role ให้แสดง loading (กำลัง redirect)
  // แต่ถ้าอยู่ใน public route ให้อนุญาตให้ผ่านได้
  const isPublicRoute = window.location.pathname.startsWith('/pharma-quiz') || 
                        window.location.pathname.startsWith('/mothai-quiz') ||
                        window.location.pathname === '/' ||
                        window.location.pathname === '/login';
  
  if (currentUser && !currentUser.role && !isPublicRoute) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">กำลังนำคุณไปหน้าสมัครสมาชิก...</div>
      </div>
    );
  }

  // ตรวจสอบ role ที่ต้องการ
  if (requiredRole && !hasRole(currentUser, requiredRole)) {
    // ถ้าเป็น admin route ให้แสดงข้อความแนะนำ
    if (requiredRole === 'admin') {
      return fallback || (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">🔐</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">ต้องการสิทธิ์ Admin</h2>
              <p className="text-gray-600 mb-6">
                คุณต้องมีสิทธิ์ Admin เพื่อเข้าถึงหน้านี้
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-800 mb-2">📋 วิธีตั้ง Admin Role:</h3>
              <ol className="text-sm text-blue-700 space-y-2 list-decimal list-inside">
                <li>Login ด้วย account ของคุณ (เบอร์โทรหรือ Email)</li>
                <li>ไปที่หน้า <strong>/set-admin</strong> เพื่อตั้ง Admin Role</li>
                <li>คลิกปุ่ม "ตั้ง Admin Role"</li>
                <li>รีเฟรชหน้าเว็บ (F5)</li>
                <li>กลับมาที่หน้านี้</li>
              </ol>
            </div>

            <div className="space-y-3">
              <a
                href="/set-admin"
                className="block w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-center"
              >
                🔧 ไปตั้ง Admin Role
              </a>
              <a
                href="/"
                className="block w-full px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition-colors text-center"
              >
                ← กลับหน้าหลัก
              </a>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                💡 <strong>หมายเหตุ:</strong> หลังจากตั้ง Admin Role แล้ว ให้รีเฟรชหน้าเว็บ (F5) 
                เพื่อให้ระบบโหลด role ใหม่
              </p>
            </div>
          </div>
        </div>
      );
    }

    return fallback || (
      <div className="p-6 text-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p className="font-semibold">❌ ไม่มีสิทธิ์เข้าถึง</p>
          <p className="text-sm mt-1">คุณไม่มีสิทธิ์เข้าถึงหน้านี้</p>
          <p className="text-xs mt-2">Role ที่ต้องการ: {requiredRole}</p>
        </div>
      </div>
    );
  }

  // ตรวจสอบ allowedRoles (array ของ roles ที่อนุญาต)
  if (allowedRoles && Array.isArray(allowedRoles) && allowedRoles.length > 0) {
    const hasAllowedRole = allowedRoles.some(role => hasRole(currentUser, role));
    if (!hasAllowedRole) {
      return fallback || (
        <div className="p-6 text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <p className="font-semibold">❌ ไม่มีสิทธิ์เข้าถึง</p>
            <p className="text-sm mt-1">คุณไม่มีสิทธิ์เข้าถึงหน้านี้</p>
            <p className="text-xs mt-2">Role ที่อนุญาต: {allowedRoles.join(', ')}</p>
          </div>
        </div>
      );
    }
  }

  // ตรวจสอบ permission ที่ต้องการ
  if (requiredPermission && !hasPermission(currentUser, requiredPermission)) {
    return fallback || (
      <div className="p-6 text-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p className="font-semibold">❌ ไม่มีสิทธิ์เข้าถึง</p>
          <p className="text-sm mt-1">คุณไม่มีสิทธิ์เข้าถึงหน้านี้</p>
          <p className="text-xs mt-2">Permission ที่ต้องการ: {requiredPermission}</p>
        </div>
      </div>
    );
  }

  // ผ่านการตรวจสอบทั้งหมด แสดงเนื้อหา
  return children;
};

export default ProtectedRoute;












